import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Clock, User, AlertTriangle } from 'lucide-react';
import { teamSchedulingService } from '@/services/teamService';
import { useAuthStore } from '@/store/authStore';
import type { TeamMember } from '@/types';

interface TeamWorkloadProps {
  teamMembers: TeamMember[];
}

interface WorkloadData {
  memberId: string;
  memberName: string;
  totalHours: number;
  scheduledHours: number;
  availableHours: number;
  utilizationRate: number;
}

export function TeamWorkload({ teamMembers: _teamMembers }: TeamWorkloadProps) {
  const { user } = useAuthStore();
  const [workloadData, setWorkloadData] = useState<WorkloadData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'week' | 'month'>('week');

  useEffect(() => {
    loadWorkloadData();
  }, [dateRange, user?.id]);

  const loadWorkloadData = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const startDate = new Date();
      const endDate = new Date();
      
      if (dateRange === 'week') {
        endDate.setDate(startDate.getDate() + 7);
      } else {
        endDate.setMonth(startDate.getMonth() + 1);
      }

      const data = await teamSchedulingService.getTeamWorkload(user.id, startDate, endDate);
      setWorkloadData(data);
    } catch (error) {
      console.error('Failed to load workload data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUtilizationColor = (rate: number) => {
    if (rate >= 90) return 'text-red-600 bg-red-100';
    if (rate >= 80) return 'text-orange-600 bg-orange-100';
    if (rate >= 60) return 'text-green-600 bg-green-100';
    return 'text-blue-600 bg-blue-100';
  };

  const getUtilizationStatus = (rate: number) => {
    if (rate >= 90) return 'Overloaded';
    if (rate >= 80) return 'High';
    if (rate >= 60) return 'Optimal';
    return 'Underutilized';
  };

  const averageUtilization = workloadData.length > 0 
    ? workloadData.reduce((sum, member) => sum + member.utilizationRate, 0) / workloadData.length 
    : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2" />
            Team Workload Analysis
          </h3>
          <p className="text-sm text-gray-600">Monitor team utilization and capacity</p>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setDateRange('week')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                dateRange === 'week' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => setDateRange('month')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                dateRange === 'month' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              This Month
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Utilization</p>
              <p className="text-2xl font-bold text-gray-900">{averageUtilization.toFixed(1)}%</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Scheduled Hours</p>
              <p className="text-2xl font-bold text-gray-900">
                {workloadData.reduce((sum, member) => sum + member.scheduledHours, 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Available Capacity</p>
              <p className="text-2xl font-bold text-gray-900">
                {workloadData.reduce((sum, member) => sum + member.availableHours, 0)}h
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <User className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Individual Member Workload */}
      <div className="card p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Individual Workload</h4>
        
        <div className="space-y-4">
          {workloadData.map((member) => (
            <div key={member.memberId} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <User className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900">{member.memberName}</h5>
                    <p className="text-sm text-gray-600">
                      {member.scheduledHours}h scheduled / {member.totalHours}h total
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getUtilizationColor(member.utilizationRate)}`}>
                    {getUtilizationStatus(member.utilizationRate)}
                  </span>
                  <span className="text-lg font-semibold text-gray-900">
                    {member.utilizationRate.toFixed(1)}%
                  </span>
                  {member.utilizationRate >= 90 && (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${
                    member.utilizationRate >= 90 ? 'bg-red-500' :
                    member.utilizationRate >= 80 ? 'bg-orange-500' :
                    member.utilizationRate >= 60 ? 'bg-green-500' :
                    'bg-blue-500'
                  }`}
                  style={{ width: `${Math.min(member.utilizationRate, 100)}%` }}
                />
              </div>

              {/* Hours Breakdown */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Scheduled:</span>
                  <span className="font-medium ml-1">{member.scheduledHours}h</span>
                </div>
                <div>
                  <span className="text-gray-600">Available:</span>
                  <span className="font-medium ml-1">{member.availableHours}h</span>
                </div>
                <div>
                  <span className="text-gray-600">Total:</span>
                  <span className="font-medium ml-1">{member.totalHours}h</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {workloadData.length === 0 && (
          <div className="text-center py-8">
            <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">No workload data available</p>
          </div>
        )}
      </div>

      {/* Recommendations */}
      {workloadData.some(member => member.utilizationRate >= 90) && (
        <div className="card p-4 border-orange-200 bg-orange-50">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-orange-600 mr-3 mt-0.5" />
            <div>
              <h4 className="font-medium text-orange-900">Workload Alert</h4>
              <p className="text-sm text-orange-800 mt-1">
                Some team members are overloaded. Consider redistributing work or hiring additional staff.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}